import PropTypes from 'prop-types';
import cn from 'classnames';
import CoCGraphViewer from '../CoCGraphViewer/cocGraphViewer';
import {statusIcons} from '../../../../../assets/svg';
import CoCAttachmentButton from '../CoCAttachmentButton/cocAttachmentButton';
import {validateFileType} from '../../utils/cocHelper';
import useLabels from 'infrastructure/js/hooks/useLabels';

import './cocAttachments.scss';

const MAX_GENERIC_ATTACHMENTS = 50;

export default function CoCAttachments({
                                         index,
                                         name,
                                         isViewOnly = false,
                                         attachmentsCounters = [],
                                         attachmentsFiles = [],
                                         onAttachmentUpload = null,
                                         onAttachmentUploadValidation = null,
                                         onAttachmentRemove = null,
                                         onAttachmentClick = null,
                                         onAttachmentDownload = null,
                                         onAttachmentResultChange = null,
                                         genericAttachmentsRequired = false,
                                         showEmptyFieldErrors = false,

                                       }) {

  const labels = useLabels('mat.forms.cocTestForm.sectionListItem.attachments.');

  const onAttachmentChanged = (file, type) => {
    if (!validateFileType(file, type)) {
      return onAttachmentUploadValidation?.({ type: 'warning',
        title: labels.get('invalidFileType.title'),
        message: labels.get('invalidFileType.message'),
      });
    }
    const exists = attachmentsFiles.some(f => f.name === file.name);
    if (exists) {
      return onAttachmentUploadValidation?.(
        { type: 'warning',
          title: labels.get('duplicatedFile.title'),
          message: labels.get('duplicatedFile.message', '', {fileName: file.name}),
        });
    }

    onAttachmentUpload?.(file, type);
  }

  const renderRemoveFileButton = (item)  => {
    return isViewOnly ? null :
      (
        <span className="pl pl-x-input-close remove-attached-file-btn"
              onClick={(e) => onFileRemoveHandler(e, item)}/>
      );
  }

  const onFileRemoveHandler = (e, item) => {
    e.preventDefault();
    e.stopPropagation();

    if (attachmentsFiles.some(it => it.id === item.id)) {
      onAttachmentRemove?.(item);
    }
  }

  const renderAttachFilesSection = () => {
    if (genericAttachmentsRequired ) {
      const attachedGenerics = attachmentsFiles.filter(f => f.type === 'GENERIC');

      return (
        <div className="attach-buttons-section">
          <span>{labels.get('attachGenericFiles')}</span>
          <CoCAttachmentButton key={`attach-btn-generic-${index}`}
                               id={`attach-btn-generic-${index}`}
                               type={'GENERIC'}
                               tooltip={''}
                               buttonLabel={labels.get('attachButton.label')}
                               disabled={isViewOnly || attachedGenerics.length >= MAX_GENERIC_ATTACHMENTS}
                               onAttachmentChanged={onAttachmentChanged}
                               emptyError={showEmptyFieldErrors && attachedGenerics.length === 0}
          />
        </div>
      )
    }

    if (!attachmentsCounters.length) {
      return null;
    }

    return (
      <div className="attach-buttons-section">
        <span>{labels.get('attachFiles')}</span>
        {attachmentsCounters.map((item) =>
          <CoCAttachmentButton key={item.type}
                               id={`attach-btn-${item.type}-${index}`}
                               {...item}
                               disabled={isViewOnly}
                               onAttachmentChanged={onAttachmentChanged}
                               emptyError={showEmptyFieldErrors && item.attachmentsCount < item.attachmentsTotal}
          /> )}
      </div>
    )
  }

  const renderAttachedFilesSection = () => {
    const attachedFiles = genericAttachmentsRequired ?
      attachmentsFiles.filter(f => f.type === 'GENERIC') :
      attachmentsFiles.filter(f => f.type !== 'GRAPH');

    if (!attachedFiles.length) {
      return null;
    }

    return (
      <div className="attached-files-section">
        {attachedFiles.map((item, itemIndex) =>
          <div key={`${itemIndex}-${item.id}`} className='attached-file' onClick={() => onAttachmentDownload?.(item)}>
            <span className='pl pl-file'/>
            <span className='file-name'>{item.name || item.file?.name}</span>
            {renderRemoveFileButton(item)}
          </div>
        )}
      </div>
    )
  }

  const renderAttachedGraphsSection = () => {
    if (genericAttachmentsRequired ) {
      return null;
    }

    const attachedGraphs = attachmentsFiles.filter(f => f.type === 'GRAPH');
    if (!attachedGraphs.length) {
      return null;
    }

    return (
      <div className="attached-graphs-section">
        {attachedGraphs.map((item, itemIndex) => {
          const isEmptyResult = showEmptyFieldErrors && (!item.testResult || item.testResult === 'N/A');
          return (
            <div key={`${itemIndex}-${item.id}`} className='attached-graph-item'>
              <div className={cn('attached-graph-file', {'empty-error': isEmptyResult})}>
                <div className={item.isExpanded ? 'pl pl-arrow-down' : 'pl pl-arrow-right'} onClick={() => onAttachmentClick?.(item)}/>
                <div className='attached-file' onClick={() => onAttachmentDownload?.(item)}>
                  <span className='pl pl-file'/>
                  <span className='file-name'>{item.name || item.file?.name}</span>
                  {renderRemoveFileButton(item)}
                </div>
                {item.testResult === 'PASS' ? <statusIcons.statusPass/> : (item.testResult === 'FAIL' ? <statusIcons.statusFail/> : null)}
              </div>

              {item.isExpanded &&
                <CoCGraphViewer graph={item} index={itemIndex}
                                name={name}
                                isViewOnly={isViewOnly}
                                onGraphResultChange={onAttachmentResultChange}
                                showEmptyFieldErrors={showEmptyFieldErrors}
                />}
            </div>
          );
        })}
      </div>
    )
  }

  return (
    <div className="coc-attachments">
      {renderAttachFilesSection()}
      {renderAttachedFilesSection()}
      {renderAttachedGraphsSection()}
    </div>
  )
}

CoCAttachments.propTypes = {
  index: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  isViewOnly: PropTypes.bool,
  attachmentsCounters: PropTypes.array,
  attachmentsFiles: PropTypes.array,
  onAttachmentUpload: PropTypes.func,
  onAttachmentUploadValidation: PropTypes.func,
  onAttachmentRemove: PropTypes.func,
  onAttachmentClick: PropTypes.func,
  onAttachmentDownload: PropTypes.func,
  onAttachmentResultChange: PropTypes.func,
  genericAttachmentsRequired: PropTypes.bool,
  showEmptyFieldErrors: PropTypes.bool,
}
